/**
 * ProfileDrawer - Redesigned profile settings panel
 *
 * Desktop: Right-side drawer with vertical sidebar tabs
 * Mobile: Full-screen modal with top tabs
 *
 * Tabs:
 * 📅 Schedule - Weekly workout preferences, preferred long day
 * ⚡ Performance - Threshold pace, FTP, weekly distance, training ratio
 * 🎯 Goals - Training goals and targets
 * 🔗 Sync - Intervals.icu connection
 * ⚙️ Settings - Advanced constants, plan management, fatigue
 */

import {
  AutoAwesome as AICoachIcon,
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
  TrackChanges as GoalsIcon,
  FlashOn as PerformanceIcon,
  Settings as SettingsIcon,
  Link as SyncIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Drawer,
  IconButton,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { UserProfile } from '@shared/types';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useEntraAuth } from '../../../contexts/EntraAuthContext';
import apiClient from '../../../services/api';
import { logger } from '../../../utils/logger';
import { loadUserProfileSync } from '../../../utils/userProfile';
import AICoachModal from '../../dashboard/AICoachModal';
import AICoachTab from './tabs/AICoachTab';
import GoalsTab from './tabs/GoalsTab';
import PerformanceTab from './tabs/PerformanceTab';
import ScheduleTab from './tabs/ScheduleTab';
import SettingsTab from './tabs/SettingsTab';
import SyncTab from './tabs/SyncTab';

interface ProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (profile: UserProfile) => Promise<void>;
  initialProfile?: UserProfile;
}

// Tab configuration
const TABS = [
  { id: 'schedule', label: 'Schedule', icon: CalendarIcon, shortLabel: 'Sched', requiresAI: false },
  {
    id: 'performance',
    label: 'Performance',
    icon: PerformanceIcon,
    shortLabel: 'Perf',
    requiresAI: false,
  },
  { id: 'goals', label: 'Goals', icon: GoalsIcon, shortLabel: 'Goals', requiresAI: true },
  { id: 'aicoach', label: 'AI Coach', icon: AICoachIcon, shortLabel: 'AI', requiresAI: true },
  { id: 'sync', label: 'Sync', icon: SyncIcon, shortLabel: 'Sync', requiresAI: false },
  { id: 'settings', label: 'Settings', icon: SettingsIcon, shortLabel: 'More', requiresAI: false },
] as const;

type TabId = (typeof TABS)[number]['id'];

// Default profile values
const DEFAULT_PROFILE: UserProfile = {
  userId: 'default_user',
  intervalsConnected: false,
  syncToIntervals: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  thresholdPaceSec: 240,
  weeklyMileage: 50,
  weeklyWorkoutPreferences: {
    Monday: ['base run'],
    Tuesday: ['hard run'],
    Wednesday: ['base run'],
    Thursday: ['base run'],
    Friday: ['rest'],
    Saturday: ['long run'],
    Sunday: ['rest'],
  },
  fatigueLevel: 'normal',
  ATL: 0,
  CTL: 0,
  HRV: 0,
  maxSessionDurationMin: 180,
};

const DRAWER_WIDTH = 520;

const ProfileDrawer: React.FC<ProfileDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  initialProfile,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState<TabId>('schedule');
  const [profile, setProfile] = useState<UserProfile>(DEFAULT_PROFILE);
  const [originalProfile, setOriginalProfile] = useState<UserProfile | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isEngineToggleDisabled, setIsEngineToggleDisabled] = useState(false);
  const [showAICoachModal, setShowAICoachModal] = useState(false);
  const [aiCoachPrompt, setAiCoachPrompt] = useState<string | undefined>(undefined);

  // Get Entra user info for display
  const { user: entraUser } = useEntraAuth();

  // Get real-time Intervals.icu connection status from AuthContext
  const { state: authState, checkAuth } = useAuth();
  const isIntervalsConnected = authState.isIntervalsConnected;

  // Get user display name from Entra or Intervals.icu
  const userDisplayName =
    entraUser?.name || entraUser?.username || authState.tokenData?.athlete?.name;

  // Load initial profile when drawer opens
  useEffect(() => {
    if (isOpen) {
      loadProfile();
      setActiveTab('schedule');
    }
  }, [isOpen]);

  // Switch to a valid tab if current tab becomes disabled (AI Engine toggled off)
  useEffect(() => {
    const currentTab = TABS.find(t => t.id === activeTab);
    if (currentTab?.requiresAI && !profile.aiEngineEnabled) {
      setActiveTab('schedule');
    }
  }, [profile.aiEngineEnabled, activeTab]);

  const loadProfile = async () => {
    const storedProfile = loadUserProfileSync();

    try {
      const backendProfile = await apiClient.getUserProfile();
      const profileToUse =
        backendProfile.success && backendProfile.data
          ? backendProfile.data
          : initialProfile || storedProfile;

      const finalProfile = {
        ...DEFAULT_PROFILE,
        ...profileToUse,
        ATL: profileToUse.ATL || storedProfile.ATL || DEFAULT_PROFILE.ATL,
        CTL: profileToUse.CTL || storedProfile.CTL || DEFAULT_PROFILE.CTL,
        HRV: profileToUse.HRV || storedProfile.HRV || DEFAULT_PROFILE.HRV,
      };

      setProfile(finalProfile);
      setOriginalProfile(finalProfile);
      setPendingChanges(new Set());
    } catch (error) {
      logger.error('Failed to fetch profile from backend', error as Error);
      const profileToUse = initialProfile || storedProfile;
      const finalProfile = {
        ...DEFAULT_PROFILE,
        ...profileToUse,
        ATL: profileToUse.ATL || storedProfile.ATL || DEFAULT_PROFILE.ATL,
        CTL: profileToUse.CTL || storedProfile.CTL || DEFAULT_PROFILE.CTL,
        HRV: profileToUse.HRV || storedProfile.HRV || DEFAULT_PROFILE.HRV,
      };
      setProfile(finalProfile);
      setOriginalProfile(finalProfile);
    }
  };

  // Handle profile updates with auto-save for simple fields
  const handleProfileUpdate = async (
    updates: Partial<UserProfile>,
    requiresConfirmation = false
  ) => {
    const newProfile = { ...profile, ...updates };
    setProfile(newProfile);

    if (requiresConfirmation) {
      // Mark these fields as pending (need confirmation to apply)
      const changedKeys = Object.keys(updates);
      setPendingChanges(prev => new Set([...prev, ...changedKeys]));
    } else {
      // Auto-save immediately for simple fields
      try {
        await onSave(newProfile);
        logger.info('Profile auto-saved', { updatedFields: Object.keys(updates) });
      } catch (error) {
        logger.error('Failed to auto-save profile', error as Error);
      }
    }
  };

  // Handle saving changes that require plan recalculation
  const handleSaveWithRecalculation = async () => {
    setIsSaving(true);
    try {
      // If AI Engine is enabled, prompt AI Coach to update the plan
      if (profile.aiEngineEnabled) {
        logger.info('AI Engine mode active - prompting AI Coach for plan update');

        // Build a prompt describing what changed
        const changedFields = Array.from(pendingChanges);
        logger.info('Changed fields for AI prompt', { changedFields });

        let promptText = 'Please update my training plan based on my updated profile settings. ';

        if (changedFields.includes('scheduleEvents') && profile.scheduleEvents?.length) {
          const events = profile.scheduleEvents;
          const eventDescriptions = events.map(e => {
            let desc = `${e.date}: ${e.eventType}`;
            if (e.location) desc += ` (${e.location})`;
            if (e.note) desc += ` - ${e.note}`;
            return desc;
          });
          promptText += `I have updated my schedule overrides: ${eventDescriptions.join('; ')}. `;
        }

        if (changedFields.includes('weeklyWorkoutPreferences')) {
          promptText += 'I have updated my weekly workout preferences. ';
        }

        if (changedFields.includes('preferredLongDay')) {
          promptText += `My preferred long run day is now ${profile.preferredLongDay}. `;
        }

        if (changedFields.includes('thresholdPaceSec')) {
          const mins = Math.floor(profile.thresholdPaceSec / 60);
          const secs = profile.thresholdPaceSec % 60;
          promptText += `My threshold pace is now ${mins}:${secs.toString().padStart(2, '0')}/km. `;
        }

        if (changedFields.includes('weeklyMileage')) {
          promptText += `My weekly mileage target is now ${profile.weeklyMileage}km. `;
        }

        promptText += 'Please adjust my upcoming workouts accordingly.';

        // Save profile first
        await onSave(profile);

        // Dispatch event to notify components about profile update
        window.dispatchEvent(new CustomEvent('user-profile-updated', { detail: profile }));

        logger.info('Opening AI Coach modal with prompt', { promptLength: promptText.length });

        setPendingChanges(new Set());
        setOriginalProfile(profile);
        setSaveSuccess(true);
        setIsSaving(false);

        // Open AI Coach modal with the prompt
        setAiCoachPrompt(promptText);
        setShowAICoachModal(true);
        return;
      }

      // Save profile for local engine mode
      await onSave(profile);

      // Dispatch event to notify components about profile update
      window.dispatchEvent(new CustomEvent('user-profile-updated', { detail: profile }));

      // Recalculate plan using local engine
      const planResponse = await apiClient.getStoredTrainingPlan();
      const existingPlan = planResponse.success ? planResponse.data : undefined;
      const response = await apiClient.generateOrUpdateTrainingPlan(profile, existingPlan);

      if (response.success && response.data) {
        await apiClient.updateStoredTrainingPlan(response.data);
        window.dispatchEvent(new CustomEvent('training-plan-updated', { detail: response.data }));
      }

      setPendingChanges(new Set());
      setOriginalProfile(profile); // Update original to reflect saved state
      setSaveSuccess(true);
    } catch (error) {
      logger.error('Failed to save and recalculate', error as Error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setPendingChanges(new Set());
    onClose();
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'schedule':
        return (
          <ScheduleTab
            profile={profile}
            onUpdate={handleProfileUpdate}
            pendingChanges={pendingChanges}
          />
        );
      case 'performance':
        return (
          <PerformanceTab
            profile={profile}
            onUpdate={handleProfileUpdate}
            pendingChanges={pendingChanges}
          />
        );
      case 'goals':
        return (
          <GoalsTab
            profile={profile}
            onUpdate={handleProfileUpdate}
            pendingChanges={pendingChanges}
          />
        );
      case 'aicoach':
        return (
          <AICoachTab
            profile={profile}
            onUpdate={handleProfileUpdate}
            onNavigateToTab={(tabId: string) => setActiveTab(tabId as TabId)}
            onClose={onClose}
          />
        );
      case 'sync':
        return (
          <SyncTab
            profile={profile}
            onUpdate={handleProfileUpdate}
            isConnected={isIntervalsConnected}
            onRefreshAuth={checkAuth}
          />
        );
      case 'settings':
        return (
          <SettingsTab
            profile={profile}
            onUpdate={handleProfileUpdate}
            pendingChanges={pendingChanges}
            onClose={onClose}
          />
        );
      default:
        return null;
    }
  };

  // Vertical tabs for desktop
  const VerticalTabs = () => (
    <Box
      sx={{
        width: 72,
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {TABS.map(tab => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        const isDisabled = tab.requiresAI && !profile.aiEngineEnabled;

        const tabContent = (
          <Box
            key={tab.id}
            onClick={() => !isDisabled && setActiveTab(tab.id)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 1.5,
              px: 1,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              bgcolor: isActive ? 'action.selected' : 'transparent',
              borderLeft: isActive ? 3 : 0,
              borderColor: 'primary.main',
              transition: 'all 0.2s',
              opacity: isDisabled ? 0.4 : 1,
              '&:hover': {
                bgcolor: isDisabled ? 'transparent' : 'action.hover',
              },
            }}
            role="tab"
            aria-selected={isActive}
            aria-disabled={isDisabled}
            aria-label={tab.label}
          >
            <Icon
              sx={{
                fontSize: 24,
                color: isDisabled ? 'text.disabled' : isActive ? 'primary.main' : 'text.secondary',
                mb: 0.5,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: isDisabled ? 'text.disabled' : isActive ? 'primary.main' : 'text.secondary',
                fontWeight: isActive ? 600 : 400,
              }}
            >
              {tab.shortLabel}
            </Typography>
          </Box>
        );

        return isDisabled ? (
          <Tooltip
            key={tab.id}
            title="Enable AI Engine mode to access this feature"
            placement="right"
            arrow
          >
            <span>{tabContent}</span>
          </Tooltip>
        ) : (
          tabContent
        );
      })}
    </Box>
  );

  // Horizontal tabs for mobile
  const HorizontalTabs = () => (
    <Tabs
      value={activeTab}
      onChange={(_, value) => {
        const tab = TABS.find(t => t.id === value);
        if (tab && !(tab.requiresAI && !profile.aiEngineEnabled)) {
          setActiveTab(value as TabId);
        }
      }}
      variant="fullWidth"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        minHeight: 56,
        '& .MuiTab-root': {
          minHeight: 56,
          minWidth: 0,
          px: 1,
        },
      }}
    >
      {TABS.map(tab => {
        const Icon = tab.icon;
        const isDisabled = tab.requiresAI && !profile.aiEngineEnabled;
        return (
          <Tab
            key={tab.id}
            value={tab.id}
            disabled={isDisabled}
            icon={
              <Icon
                sx={{
                  fontSize: 20,
                  opacity: isDisabled ? 0.4 : 1,
                }}
              />
            }
            label={
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  mt: 0.25,
                  opacity: isDisabled ? 0.4 : 1,
                }}
              >
                {tab.shortLabel}
              </Typography>
            }
            aria-label={isDisabled ? `${tab.label} (requires AI Engine)` : tab.label}
            sx={{
              flexDirection: 'column',
              gap: 0,
            }}
          />
        );
      })}
    </Tabs>
  );

  // Header with title, engine toggle, and close button
  const Header = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="h6" fontWeight={600}>
          Profile Settings{userDisplayName && ` – ${userDisplayName}`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Engine Mode Toggle */}
        <Tooltip
          title={
            profile.aiEngineEnabled
              ? 'Switch to Local Engine (rules-based)'
              : 'Switch to AI Engine (AI agents manage plans)'
          }
          arrow
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 2,
              bgcolor: profile.aiEngineEnabled
                ? 'rgba(156, 39, 176, 0.1)'
                : 'rgba(158, 158, 158, 0.1)',
              border: 1,
              borderColor: profile.aiEngineEnabled ? 'secondary.main' : 'grey.400',
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{
                color: profile.aiEngineEnabled ? 'secondary.main' : 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {profile.aiEngineEnabled ? '🤖 AI' : '⚙️ Local'}
            </Typography>
            <Switch
              checked={profile.aiEngineEnabled === true}
              onChange={e => {
                handleProfileUpdate({ aiEngineEnabled: e.target.checked });
                // Disable toggle briefly to prevent rapid switching
                setIsEngineToggleDisabled(true);
                setTimeout(() => setIsEngineToggleDisabled(false), 1000);
              }}
              disabled={isEngineToggleDisabled}
              size="small"
              color="secondary"
              aria-label="Toggle between AI Engine and Local Engine"
              sx={{ ml: -0.5, mr: -1 }}
            />
          </Box>
        </Tooltip>
        <IconButton onClick={handleClose} size="small" aria-label="Close profile settings">
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );

  // Discard pending changes and revert UI to original state
  const handleDiscardChanges = () => {
    if (originalProfile) {
      setProfile({ ...originalProfile });
    }
    setPendingChanges(new Set());
  };

  // Pending changes banner with styled buttons
  const PendingChangesBanner = () =>
    pendingChanges.size > 0 ? (
      <Box
        sx={{
          p: 2,
          background: profile.aiEngineEnabled
            ? 'linear-gradient(135deg, #7c4dff 0%, #651fff 100%)'
            : 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          borderBottom: 1,
          borderColor: profile.aiEngineEnabled ? 'secondary.dark' : 'warning.dark',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 150 }}>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.3 }}>
            {pendingChanges.size} unsaved change{pendingChanges.size > 1 ? 's' : ''}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            {profile.aiEngineEnabled
              ? '🤖 AI Engine: Profile saved, plan managed by AI agents'
              : '⚙️ Local Engine: Will recalculate your training plan'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleDiscardChanges}
            aria-label="Discard changes"
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 80,
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSaveWithRecalculation}
            disabled={isSaving}
            aria-label="Save and recalculate training plan"
            sx={{
              bgcolor: 'white',
              color: '#e65100',
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 80,
              boxShadow: 2,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.9)',
                boxShadow: 4,
              },
            }}
          >
            {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </Box>
      </Box>
    ) : null;

  // Success notification Snackbar
  const SuccessSnackbar = (
    <Snackbar
      open={saveSuccess}
      autoHideDuration={3000}
      onClose={(_, reason) => {
        // Only close snackbar on timeout, not on clickaway
        if (reason === 'timeout') {
          setSaveSuccess(false);
        }
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      ClickAwayListenerProps={{
        onClickAway: () => {
          // Prevent click away from closing snackbar or propagating
        },
      }}
    >
      <Alert
        onClose={() => setSaveSuccess(false)}
        severity="success"
        variant="filled"
        sx={{ width: '100%' }}
      >
        Profile saved successfully!
      </Alert>
    </Snackbar>
  );

  // Desktop: Right-side drawer with vertical tabs
  if (!isMobile) {
    return (
      <>
        <Drawer
          anchor="right"
          open={isOpen}
          onClose={(_, reason) => {
            // Only close on explicit backdrop click or escape key
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              handleClose();
            }
          }}
          slotProps={{
            backdrop: {
              sx: {
                backdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
          }}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              maxWidth: '100vw',
            },
          }}
        >
          <Header />
          <PendingChangesBanner />
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            <VerticalTabs />
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
              }}
            >
              {renderTabContent()}
            </Box>
          </Box>
          {SuccessSnackbar}
        </Drawer>

        {/* AI Coach Modal - opened after saving with AI Engine enabled */}
        <AICoachModal
          isOpen={showAICoachModal}
          onClose={() => {
            setShowAICoachModal(false);
            setAiCoachPrompt(undefined);
          }}
          onChangesApplied={() => {
            setShowAICoachModal(false);
            setAiCoachPrompt(undefined);
            // Dispatch event to update the training plan display
            window.dispatchEvent(new CustomEvent('training-plan-updated'));
          }}
          initialPrompt={aiCoachPrompt}
        />
      </>
    );
  }

  // Mobile: Full-screen dialog with horizontal tabs
  return (
    <>
      <Dialog
        open={isOpen}
        onClose={(_, reason) => {
          // Only close on explicit backdrop click or escape key
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            handleClose();
          }
        }}
        fullScreen
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
        PaperProps={{
          sx: {
            bgcolor: 'background.default',
          },
        }}
      >
        <Header />
        <HorizontalTabs />
        <PendingChangesBanner />
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
          }}
        >
          {renderTabContent()}
        </Box>
        {SuccessSnackbar}
      </Dialog>

      {/* AI Coach Modal - opened after saving with AI Engine enabled */}
      <AICoachModal
        isOpen={showAICoachModal}
        onClose={() => {
          setShowAICoachModal(false);
          setAiCoachPrompt(undefined);
        }}
        onChangesApplied={() => {
          setShowAICoachModal(false);
          setAiCoachPrompt(undefined);
          // Dispatch event to update the training plan display
          window.dispatchEvent(new CustomEvent('training-plan-updated'));
        }}
        initialPrompt={aiCoachPrompt}
      />
    </>
  );
};

export default ProfileDrawer;
